import ReactFinalForm from 'components/form/ReactFinalForm';
import { IOption, IStatusOption } from 'interfaces';
import { IdentityField } from 'components/form/IdentityField';
import { useTranslation } from 'react-i18next';
import { composeValidators, mustBeNumber } from 'helpers/validators';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';

// Interface to define the parameters for the UpdateContractLineDialog component
interface UpdateContractLineDialogFormParams {
  contractLine: any; // Represents the contract line data
  warehouses: IOption[]; // List of warehouse options for the dropdown
  statusOptions: IStatusOption[]; // List of status options (ProductWarehouse.Status)
  onSubmit: any; // Callback function to handle form submission
  contract: string; // Contract code for dialog
}

// Function component to update contract line details
function UpdateContractLineDialog(params: UpdateContractLineDialogFormParams) {
  const { t } = useTranslation();
  const { contractLine, warehouses, statusOptions, onSubmit, contract } = params;

  // Translation prefix for easier access to translation keys
  const translationPrefix = 'pages.contract-line.edit';

  // Function to handle form submission
  const onSubmitContractLine = async (values) => {
    // Prepare parameters for the submit function
    const params = {
      id: contractLine.id,
      unit_price: values.unit_price.replace(',', '.'), // Replace comma with dot for unit price
      initial_available_units: values.initial_available_units,
      initial_discounted_units: values.initial_discounted_units,
      iva: values.iva,
      warehouse: values.warehouse || null,
      status: values.status || contractLine.status || null
    };

    // Call the onSubmit callback with contract line ID and prepared parameters
    onSubmit(contractLine.id, params);
  };

  // calcola il valore iniziale dello status (match per value o display_name)
  const initialStatusOption = statusOptions?.find((opt) => {
    if (contractLine.status === undefined || contractLine.status === null) return false;
    const statusAsString = String(contractLine.status);
    return String(opt.value) === statusAsString || String(opt.display_name) === statusAsString;
  });

  return (
    <div>
      {/* Display contract line product information */}
      <div className="align-top p-3">
        {t(`${translationPrefix}.body`, { product: contractLine.product.code, contract: contract })}
      </div>
      {/* Form component to handle updating contract line */}
      <ReactFinalForm onSubmit={onSubmitContractLine} formId="updateContracLine" hideControls>
        <div className="flex flex-col gap-3">
          {/* Row 1: product + warehouse */}
          <div className="flex flex-wrap">
            <div className="w-full md:w-1/2 mb-2 pr-0 md:pr-4">
              <IdentityField
                disabled
                component={TextFieldAdapter}
                id="outlined-disabled"
                name="product"
                label={t('global.product')}
                type="text"
                defaultValue={contractLine.product.code}
              />
            </div>
            <div className="w-full md:w-1/2 mb-2">
              <IdentityField
                name="warehouse"
                component={AutocompleteAdapter}
                options={warehouses}
                optionValue="id"
                label={t('global.warehouse')}
                type="text"
                initValue={contractLine.warehouse}
                defaultValue={contractLine.warehouse}
              />
            </div>
          </div>

          {/* Row 2: numeric fields */}
          <div className="flex flex-wrap">
            <div className="w-full md:w-1/4 mb-2 pr-0 md:pr-2 flex">
              <IdentityField
                name="unit_price"
                label={t('entities.contract_line.unit_price')}
                component={TextFieldAdapter}
                type="number"
                validate={composeValidators(mustBeNumber)}
                defaultValue={contractLine.unit_price}
              />
            </div>
            <div className="w-full md:w-1/4 mb-2 pr-0 md:pr-2 flex">
              <IdentityField
                name="initial_available_units"
                label={t('entities.contract_line.initial_available_units')}
                component={TextFieldAdapter}
                type="number"
                validate={composeValidators(mustBeNumber)}
                defaultValue={contractLine.initial_available_units}
              />
            </div>
            <div className="w-full md:w-1/4 mb-2 pr-0 md:pr-2 flex">
              <IdentityField
                name="initial_discounted_units"
                label={t('entities.contract_line.initial_discounted_units')}
                component={TextFieldAdapter}
                type="number"
                validate={composeValidators(mustBeNumber)}
                defaultValue={contractLine.initial_discounted_units}
              />
            </div>
            <div className="w-full md:w-1/4 mb-2 flex">
              <IdentityField
                name="iva"
                label={t('entities.contract_line.iva')}
                component={TextFieldAdapter}
                type="number"
                validate={composeValidators(mustBeNumber)}
                defaultValue={contractLine.iva}
              />
            </div>
          </div>

          {/* Row 3: status */}
          <div className="flex">
            <div className="w-full md:w-1/3 mb-2">
              <IdentityField
                name="status"
                component={AutocompleteAdapter}
                initValue={initialStatusOption}
                options={statusOptions}
                optionLabel="display_name"
                optionValue="value"
                label={t('entities.product.status')}
                type="text"
              />
            </div>
          </div>
        </div>
      </ReactFinalForm>
    </div>
  );
}

export default UpdateContractLineDialog;
